// Per-line row for the receipt-extraction Review modal (slice 4f).
// Renders one extracted line item with editable qty / unit_cost, a
// searchable material picker pre-filled from the server-side
// suggestion (with an inline "+ Create new material" footer for the
// no-match case), and a "Post" button that ships the row through
// postMaterialCostEntry with receipt_id set. Once posted, the row
// collapses to a checkmark + the posted entry's id so the operator
// can see what's been written.
import { useMemo } from "react";
import { createMaterial } from "@/lib/manufacturingClient";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import {
  formatReceiveConversionSourceLabel,
  resolveReceiveConversionFactor,
  resolveReceiveConversionSource,
  shouldOfferReceiveUnitToggle,
} from "@/lib/manufacturing/receiveQtyConversion";
import { mfgMicroInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type {
  MfgReceiptExtractedLineItem,
  MfgReceiptLineMatchSuggestion,
} from "@/lib/manufacturing/types";
import {
  ManufacturingComboboxPicker,
  type ComboboxOption,
} from "./ManufacturingComboboxPicker";

export type ExtractedLineDraft = {
  description: string;
  quantity: string;
  unit_cost: string;
  notes: string;
  material_id: string;
  /** UQ-2: alias conversion from match suggestion (cleared if material changes). */
  alias_id: string | null;
  alias_unit_conversion_factor: number | null;
  alias_vendor_name: string | null;
  /** When set, "Post" is disabled and the row shows a posted indicator
   *  with this entry id (server returns it from postMaterialCostEntry). */
  posted_entry_id: string | null;
  /** Per-row error from the last Post attempt, if any. */
  error: string | null;
};

export function draftFromExtractedLine(
  line: MfgReceiptExtractedLineItem,
  suggestion: MfgReceiptLineMatchSuggestion | null
): ExtractedLineDraft {
  const aliasFactor =
    suggestion &&
    typeof suggestion.unit_conversion_factor === "number" &&
    suggestion.unit_conversion_factor > 0
      ? suggestion.unit_conversion_factor
      : null;
  return {
    description: line.description,
    quantity: line.quantity != null ? String(line.quantity) : "",
    unit_cost: line.unit_cost != null ? String(line.unit_cost) : "",
    notes: line.notes,
    material_id: suggestion?.material_id ?? "",
    alias_id: suggestion?.alias_id ?? null,
    alias_unit_conversion_factor: aliasFactor,
    alias_vendor_name: suggestion?.vendor_name ?? null,
    posted_entry_id: null,
    error: null,
  };
}

function confidenceLabel(source: MfgReceiptLineMatchSuggestion["source"]): string {
  if (source === "vendor_sku") return "SKU";
  if (source === "alias_description") return "alias";
  return "name";
}

function confidenceClass(source: MfgReceiptLineMatchSuggestion["source"]): string {
  if (source === "vendor_sku")
    return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/35 dark:text-emerald-200 dark:border-emerald-900";
  if (source === "alias_description")
    return "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/35 dark:text-amber-200 dark:border-amber-900";
  return "bg-gray-50 text-gray-700 border-gray-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700";
}

type Props = {
  line: MfgReceiptExtractedLineItem;
  draft: ExtractedLineDraft;
  suggestion: MfgReceiptLineMatchSuggestion | null;
  materials: MfgMaterial[];
  working: boolean;
  onDraftChange: (next: ExtractedLineDraft) => void;
  onPost: () => void;
  /** Called after a successful "Create new material" inline action so
   *  the modal can prepend the new material to its local catalog and
   *  refresh the parent list. */
  onMaterialCreated?: (material: MfgMaterial) => void;
};

function materialOptions(materials: MfgMaterial[]): ComboboxOption<MfgMaterial>[] {
  return materials.map((m) => ({
    key: m.id,
    label: m.name,
    sublabel: m.code,
    data: m,
  }));
}

export function ManufacturingReceiptExtractedLineRow({
  line,
  draft,
  suggestion,
  materials,
  working,
  onDraftChange,
  onPost,
  onMaterialCreated,
}: Props) {
  const isPosted = draft.posted_entry_id != null;
  const qtyNum = Number(draft.quantity);
  const costNum = Number(draft.unit_cost);
  const canPost =
    !working &&
    !isPosted &&
    draft.material_id !== "" &&
    Number.isFinite(qtyNum) &&
    qtyNum > 0 &&
    Number.isFinite(costNum) &&
    costNum >= 0;

  const options = useMemo(() => materialOptions(materials), [materials]);
  const createDefaultUnit = (line.unit && line.unit.trim()) || "each";
  const selectedMaterial = materials.find((m) => m.id === draft.material_id);
  const conversionFactor = resolveReceiveConversionFactor({
    piecesPerShippingUnit: Number(selectedMaterial?.pieces_per_shipping_unit) || 1,
    aliasUnitConversionFactor: draft.alias_unit_conversion_factor,
  });
  const conversionSource = resolveReceiveConversionSource({
    aliasUnitConversionFactor: draft.alias_unit_conversion_factor,
    aliasVendorName: draft.alias_vendor_name,
  });
  const showConversionHint =
    Boolean(draft.material_id) &&
    (shouldOfferReceiveUnitToggle({
      unit: selectedMaterial?.unit ?? "each",
      shipping_unit: selectedMaterial?.shipping_unit ?? "",
    }) ||
      conversionFactor !== 1);

  async function handleCreateMaterial(query: string) {
    const r = await createMaterial({
      name: query,
      unit: createDefaultUnit,
      category: "general",
    });
    if (!r.ok) return { error: r.error };
    const created = r.data.material;
    onMaterialCreated?.(created);
    return {
      option: {
        key: created.id,
        label: created.name,
        sublabel: created.code,
        data: created,
      } as ComboboxOption<MfgMaterial>,
    };
  }

  function pickMaterial(materialId: string) {
    // Manual material change drops the matched-alias conversion (operator
    // can still use catalog pieces_per_shipping_unit on post).
    const keepAlias =
      suggestion?.material_id === materialId && Boolean(suggestion.alias_id);
    onDraftChange({
      ...draft,
      material_id: materialId,
      alias_id: keepAlias ? (suggestion?.alias_id ?? null) : null,
      alias_unit_conversion_factor: keepAlias
        ? suggestion?.unit_conversion_factor && suggestion.unit_conversion_factor > 0
          ? suggestion.unit_conversion_factor
          : null
        : null,
      alias_vendor_name: keepAlias ? (suggestion?.vendor_name ?? null) : null,
    });
  }

  return (
    <li
      className={`rounded-lg border px-3 py-2 ${
        isPosted
          ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30"
          : "border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-xs font-medium text-gray-900 dark:text-zinc-100">
              {line.description}
            </span>
            {line.vendor_sku ? (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-gray-700 dark:bg-zinc-800 dark:text-zinc-300">
                {line.vendor_sku}
              </span>
            ) : null}
            {suggestion ? (
              <span
                title={`match score ${suggestion.score.toFixed(2)}`}
                className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${confidenceClass(suggestion.source)}`}
              >
                {confidenceLabel(suggestion.source)} ·{" "}
                {Math.round(suggestion.score * 100)}%
              </span>
            ) : (
              <span className="rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
                no match
              </span>
            )}
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_70px_90px_auto]">
            <ManufacturingComboboxPicker<MfgMaterial>
              value={draft.material_id}
              options={options}
              placeholder="— pick material —"
              disabled={isPosted}
              initialQuery={draft.material_id ? "" : line.description}
              onPick={(o) => pickMaterial(o.key)}
              onCreate={handleCreateMaterial}
              createLabel="+ Create new material"
            />
            <input
              className={mfgMicroInputClass}
              placeholder="qty"
              inputMode="decimal"
              value={draft.quantity}
              onChange={(e) => onDraftChange({ ...draft, quantity: e.target.value })}
              disabled={isPosted}
            />
            <input
              className={mfgMicroInputClass}
              placeholder="$ / unit"
              inputMode="decimal"
              value={draft.unit_cost}
              onChange={(e) => onDraftChange({ ...draft, unit_cost: e.target.value })}
              disabled={isPosted}
            />
            <button
              type="button"
              onClick={onPost}
              disabled={!canPost}
              className="rounded-md border border-indigo-300 bg-indigo-600 px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500 dark:disabled:border-zinc-700 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-500"
            >
              {isPosted ? "Posted" : working ? "…" : "Post"}
            </button>
          </div>
          {showConversionHint && !isPosted ? (
            <div className="mt-1.5 text-[11px] text-gray-500 dark:text-zinc-400">
              Posts as ×{conversionFactor} {selectedMaterial?.unit || "unit"} ·{" "}
              {formatReceiveConversionSourceLabel(conversionSource)}
            </div>
          ) : null}
          {draft.error ? (
            <div className="mt-1.5 text-[11px] text-red-700 dark:text-red-300">{draft.error}</div>
          ) : null}
          {isPosted ? (
            <div className="mt-1.5 text-[11px] text-emerald-700 dark:text-emerald-300">
              Posted to ledger (entry {draft.posted_entry_id?.slice(0, 8)}…)
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}
